import type { BulkCounts, BulkOutcome, BulkResult } from "@/entities/ops/api";

/**
 * Patch 37.4.43 · Client-side derivation of ``BulkCounts`` from a
 * ``BulkResult``. Prefer ``result.counts`` (server-derived) when
 * present — this helper exists only for backward-compat with
 * pre-37.4.43 backend responses.
 */
export function deriveBulkCounts(result: BulkResult): BulkCounts {
  if (result.counts) return result.counts;
  const counts: BulkCounts = {
    sent: 0,
    already_at_status: 0,
    illegal_transition: 0,
    blocked_spec: 0,
    not_found: 0,
    not_implemented_until_m6: 0,
    needs_recovery: 0,
    error: 0,
  };
  for (const row of result.outcomes) {
    switch (row.status) {
      case "ok":
        counts.sent += 1;
        break;
      case "already_at_status":
        counts.already_at_status += 1;
        break;
      case "illegal_transition":
        counts.illegal_transition += 1;
        break;
      case "asset_not_final_deliverable":
        counts.blocked_spec += 1;
        break;
      case "not_found":
        counts.not_found += 1;
        break;
      case "not_implemented_until_m6":
        counts.not_implemented_until_m6 += 1;
        break;
      case "needs_recovery":
        counts.needs_recovery = (counts.needs_recovery ?? 0) + 1;
        break;
      default:
        counts.error += 1;
    }
  }
  return counts;
}

/**
 * Founder-facing summary sentence built from a ``BulkCounts``. The
 * sentence ONLY renders bucket labels that actually happened, so a
 * clean success reads "5 approved" and a mixed run reads
 * "3 approved · 2 already approved · 1 blocked (render first)".
 *
 * The ``verb`` and ``pastVerb`` shape the sentence for approve / return /
 * archive without duplicating the phrasing per call site.
 */
export function summariseBulkCounts(
  counts: BulkCounts,
  labels: {
    verbPast: string; // "Approved" / "Returned" / "Archived"
    alreadyLabel: string; // "already approved" / "already in review" / "already archived"
  },
): string {
  const parts: string[] = [];
  if (counts.sent > 0) parts.push(`${counts.sent} ${labels.verbPast.toLowerCase()}`);
  if (counts.already_at_status > 0) parts.push(`${counts.already_at_status} ${labels.alreadyLabel}`);
  if (counts.illegal_transition > 0) parts.push(`${counts.illegal_transition} ineligible`);
  if (counts.blocked_spec > 0) parts.push(`${counts.blocked_spec} blocked (render first)`);
  if (counts.not_found > 0) parts.push(`${counts.not_found} not found`);
  if (counts.not_implemented_until_m6 > 0)
    parts.push(`${counts.not_implemented_until_m6} pending M6`);
  if ((counts.needs_recovery ?? 0) > 0)
    parts.push(`${counts.needs_recovery} need recovery`);
  if (counts.error > 0) parts.push(`${counts.error} failed`);
  return parts.length > 0 ? parts.join(" · ") : "Nothing to update";
}

/** Bulk actions consider these outcomes non-eligible so the CTA can
 *  be disabled with an accurate tooltip. */
export function isBulkErrorOutcome(o: BulkOutcome): boolean {
  return (
    o.status !== "ok"
    && o.status !== "already_at_status"
  );
}
